// ─────────────────────────────────────────────────────────────
// Notification Types - Single source of truth for Toast & NotificationCenter
// ─────────────────────────────────────────────────────────────

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'progress' | 'quanta' | 'dropped';

// Icon keys - serializable strings that map to React components at render time
export type NotificationIconKey =
  | 'externalLink'
  | 'copy'
  | 'check'
  | 'plus'
  | 'info'
  | 'telegram'
  | 'discord'
  | 'twitter'
  | 'arrowTopRight'
  | 'chevronRight';

// ─────────────────────────────────────────────────────────────
// Serializable action configs (storable in localStorage)
// ─────────────────────────────────────────────────────────────

/** Serializable app-handled button actions (hydrated to onClick at render time). */
export type NotificationAppAction = 'open_swap_drawer' | 'skip_pending_swap';

export interface NotificationActionButton {
  label: string;
  variant: 'primary' | 'secondary';
  trailingIcon?: NotificationIconKey;
  leadingIcon?: NotificationIconKey;
  href?: string;
  dismissOnClick?: boolean;
  /** App-handled action; mutually exclusive with href for CTA buttons. */
  appAction?: NotificationAppAction;
}

export interface NotificationActionLink {
  label: string;
  href: string;
  target?: '_blank';
  icon?: NotificationIconKey;
  color?: string;
  /** Non-clickable detail next to the link (e.g. amounts for a swap leg). */
  secondaryText?: string;
}

/** Phases for progressive SwapDrawer tracking in Notification Center. */
export type UnitUsdcSwapPhase =
  | 'wrap_broadcast'
  | 'wrap_confirming'
  | 'minting'
  | 'minted'
  | 'awaiting_evm_sign'
  | 'evm_submitted'
  | 'completed'
  | 'failed'
  | 'skipped';

export type SwapFlowId = 'unit_to_usdc' | 'unit_to_wunit' | 'wunit_to_unit' | 'wunit_to_usdc' | 'usdc_to_wunit' | 'usdc_to_unit';

/**
 * Exact leg of a swap, numbered by its position on that route.
 * The same leg can use a different number on a one-step route than on a two-step route.
 * `failed` and `skipped` are not stages; they are `swapPhase` values only.
 */
export const SWAP_STAGES = [
  '1_usdc_to_wunit_confirming',
  '2_unwrap_signature_required',
  '3_wunit_burn_confirming',
  '4_unit_release_confirming',
  '5_completed',
  '1_unit_deposit_confirming',
  '2_wunit_minting',
  '3_wunit_to_usdc_signature_required',
  '4_wunit_to_usdc_confirming',
  '1_wunit_burn_confirming',
  '2_unit_release_confirming',
  '3_completed',
  '1_wunit_to_usdc_confirming',
  '2_completed',
] as const;

export type SwapStage = (typeof SWAP_STAGES)[number];

/** Items saved before `swapStage`. Read only; new writes use `swapStage`. */
export type LegacyUsdcUnitSwapStep = 'usdc_swap' | 'unwrap_sign' | 'unit_release' | 'complete';

export interface NotificationMetadata {
  txid?: string;
  actionType?: string;
  telegramOpenUrl?: string;
  telegramManualCommand?: string;
  /** Progressive SwapDrawer swap. `unit_to_usdc` kept for persisted items. */
  swapFlow?: SwapFlowId;
  /** Derived view for the badge and Continue. `swapStage` is the source of truth. */
  swapPhase?: UnitUsdcSwapPhase;
  /** Numbered leg for this route. A later write cannot replace it with a lower number. */
  swapStage?: SwapStage;
  /** @deprecated Replaced by `swapStage`. Still read so older items can be migrated. */
  swapStep?: LegacyUsdcUnitSwapStep;
  wrapIntentId?: string;
  wrapTxid?: string;
  /** Advances only when the same submitted deposit is reconciled after a failed notification. */
  bridgeRecoveryRevision?: number;
  mintTxHash?: string;
  swapTxHash?: string;
  amountDisplay?: string;
  usdcAmountDisplay?: string;
  fromToken?: string;
  toToken?: string;
}

// ─────────────────────────────────────────────────────────────
// NOTIFICATION TYPE - used by Toast, NotificationCenter, and Notification Store
// ─────────────────────────────────────────────────────────────

export interface NotificationItem {
  id: string;
  /** Distinguishes repeated uses of a caller-supplied notification id. */
  instanceId?: string;
  type: NotificationType;
  title: string;
  description?: string;
  metadata?: NotificationMetadata;
  actionButtons?: NotificationActionButton[];
  actionLinks?: NotificationActionLink[];
  // Persistence fields (managed by store)
  isViewed: boolean;
  createdAt: number;
}

// Input when creating a notification (some fields are auto-generated by store)
export type NotificationInput = Omit<NotificationItem, 'id' | 'isViewed' | 'createdAt'> & {
  id?: string;
};
